import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Image, PanResponder, Platform, View } from 'react-native';
import type { ImageSourcePropType, ImageStyle, LayoutChangeEvent } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';
import { useT } from '@transifex/react';
import { GLASS_CONTENT_LAYER, GlassBackdrop, Logo, Text, cn as dsCn, useMotion, useTheme } from '@vidahealth/vida-ds';

import { testId } from '../../utils/testId';
import { BaseButton } from '../BaseButton';
import { useBrandSlides } from './slides';
import type { BrandSlide, SlideImage } from './slides';

const tid = (element: string, index?: number) => testId('BrandCarousel', element, index);

const SLIDE_MS = 5000;
const FADE_MS = 600;
const LINE_MS = 560;
const LINE_STAGGER_MS = 110;
const SWIPE_DISTANCE = 40;
const SWIPE_VELOCITY = 0.5;
const TRACK_WIDTH = 36;
const useNativeDriver = Platform.OS !== 'web';

type Variant = 'panel' | 'fullscreen';
type Frame = { width: number; height: number };

// Each screen mounts its own AuthLayout; carry the slide, a member's pause and the frame across navigations.
const session: { active: number; paused: boolean | null; frames: Partial<Record<Variant, Frame>> } = {
  active: 0,
  paused: null,
  frames: {},
};
// Screens under a pushed one stay mounted, so a jump or a pause reaches every carousel, not just the visible one.
const slideListeners = new Set<(slide: number) => void>();
const pauseListeners = new Set<(paused: boolean) => void>();

const goTo = (slide: number) => {
  session.active = slide;
  slideListeners.forEach((listener) => listener(slide));
};

const PlayPauseIcon = ({ paused, color }: { paused: boolean; color: string }) => (
  <Svg width={18} height={18} viewBox='0 0 18 18' fill='none'>
    <Path
      d={
        paused
          ? 'M5.25 3.9v10.2a.75.75 0 0 0 1.15.63l8.02-5.1a.75.75 0 0 0 0-1.26L6.4 3.27a.75.75 0 0 0-1.15.63Z'
          : 'M11.25 2.25H13.5c.41 0 .75.34.75.75v12c0 .41-.34.75-.75.75h-2.25a.75.75 0 0 1-.75-.75V3c0-.41.34-.75.75-.75ZM4.5 2.25h2.25c.41 0 .75.34.75.75v12c0 .41-.34.75-.75.75H4.5a.75.75 0 0 1-.75-.75V3c0-.41.34-.75.75-.75Z'
      }
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

// Takes the device crop nearest the frame's shape. RN's Image has no object-position, so it covers the frame by hand.
const coverImage = (images: SlideImage[], frame: Frame): { source: ImageSourcePropType; style: ImageStyle } => {
  const misfit = (image: SlideImage) => Math.abs(Math.log((image.aspect * frame.height) / frame.width));
  const { source, aspect, focus } = images.reduce((best, image) => (misfit(image) < misfit(best) ? image : best));
  const width = Math.max(frame.width, frame.height * aspect);
  const height = width / aspect;
  return {
    source,
    style: {
      position: 'absolute',
      width,
      height,
      left: (frame.width - width) * focus.x,
      top: (frame.height - height) * focus.y,
    },
  };
};

const LINE_RISE: Record<Variant, number> = { panel: 38, fullscreen: 32 };

const TitleLine = ({ text, order, variant }: { text: string; order: number; variant: Variant }) => {
  const { reduced } = useMotion();
  const shown = useRef(new Animated.Value(reduced ? 1 : 0)).current;

  useEffect(() => {
    if (reduced) {
      shown.setValue(1);
      return undefined;
    }
    const reveal = Animated.timing(shown, {
      toValue: 1,
      delay: order * LINE_STAGGER_MS,
      duration: LINE_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver,
    });
    reveal.start();
    return () => reveal.stop();
  }, [order, reduced, shown]);

  return (
    <View className='overflow-hidden'>
      <Animated.View
        style={{
          opacity: shown,
          transform: [{ translateY: shown.interpolate({ inputRange: [0, 1], outputRange: [LINE_RISE[variant], 0] }) }],
        }}
      >
        <Text variant={variant === 'fullscreen' ? 'h3' : 'h2'} color='glass-on-dark' testID={tid('title-line', order)}>
          {text}
        </Text>
      </Animated.View>
    </View>
  );
};

// Each line rises out from under its own edge; keyed per slide, so every change replays it.
const SlideTitle = ({ slide, variant }: { slide: BrandSlide; variant: Variant }) => {
  const lines = slide.title.split('\n');

  return (
    <View accessible focusable={false} accessibilityLabel={lines.join(' ')} testID={tid(`title-${slide.key}`)}>
      {lines.map((line, order) => (
        <TitleLine key={`${order}-${line}`} text={line} order={order} variant={variant} />
      ))}
    </View>
  );
};

type BrandCarouselProps = {
  // `panel` sits in the desktop/tablet container; `fullscreen` fills a phone behind the sheet.
  variant?: Variant;
  // Rendered under the slide copy, e.g. the phone entry's Get started / Log in buttons.
  footer?: React.ReactNode;
  // A sheet covers the landing: the slideshow holds and the logo turns white.
  behindSheet?: boolean;
  // A screen pushed on top hides this one, so only the visible carousel sets the pace.
  hidden?: boolean;
};

const INSETS = {
  panel: { top: 'inset-x-ds-48 top-ds-48', bottom: 'bottom-ds-48 left-ds-48 right-ds-48 max-w-[520px]' },
  fullscreen: { top: 'inset-x-ds-24 top-0', bottom: 'bottom-ds-16 left-ds-24 right-ds-24' },
} as const;

export const BrandCarousel = ({
  variant = 'panel',
  footer,
  behindSheet = false,
  hidden = false,
}: BrandCarouselProps) => {
  const t = useT();
  const slides = useBrandSlides();
  const { colors } = useTheme();
  // Full screen on a phone sits under the status bar and home indicator. Read softly: no provider means no insets.
  const insets = useContext(SafeAreaInsetsContext);
  const safeTop = variant === 'fullscreen' ? (insets?.top ?? 0) : 0;
  const safeBottom = variant === 'fullscreen' ? (insets?.bottom ?? 0) : 0;
  const [frame, setFrame] = useState<Frame | null>(session.frames[variant] ?? null);
  const [active, setActive] = useState(session.active);
  const [paused, setPaused] = useState(session.paused ?? false);
  const running = !paused && !behindSheet && !hidden;
  const progress = useRef(new Animated.Value(0)).current;
  const heldAt = useRef<{ slide: number; value: number } | null>(null);
  const opacities = useRef(slides.map((_, i) => new Animated.Value(i === session.active ? 1 : 0))).current;

  useEffect(() => {
    if (session.paused !== null) return;
    AccessibilityInfo.isReduceMotionEnabled().then((reduce) => {
      if (reduce) setPaused(true);
    });
  }, []);

  useEffect(() => {
    slideListeners.add(setActive);
    pauseListeners.add(setPaused);
    return () => {
      slideListeners.delete(setActive);
      pauseListeners.delete(setPaused);
    };
  }, []);

  useEffect(() => {
    session.paused = paused;
  }, [paused]);

  const togglePause = () => {
    const next = !paused;
    pauseListeners.forEach((listener) => listener(next));
  };

  // A held slide resumes its fill; a new one starts empty.
  useEffect(() => {
    const from = heldAt.current?.slide === active ? heldAt.current.value : 0;
    progress.setValue(from);
    heldAt.current = running ? null : { slide: active, value: from };
    if (!running) return undefined;
    Animated.timing(progress, {
      toValue: 1,
      duration: SLIDE_MS * (1 - from),
      easing: Easing.linear,
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished) goTo((active + 1) % slides.length);
    });
    return () => progress.stopAnimation((value) => (heldAt.current = { slide: active, value }));
  }, [active, running, progress, slides.length]);

  useEffect(() => {
    Animated.parallel(
      opacities.map((opacity, i) =>
        Animated.timing(opacity, {
          toValue: i === active ? 1 : 0,
          duration: FADE_MS,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver,
        })
      )
    ).start();
  }, [active, opacities]);

  const swipe = useMemo(() => {
    const step = (delta: number) => goTo((session.active + delta + slides.length) % slides.length);
    return PanResponder.create({
      // Only a mostly sideways drag is a swipe, so taps and vertical scrolling pass through.
      onMoveShouldSetPanResponder: (_, g) => !behindSheet && Math.abs(g.dx) > 14 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderRelease: (_, g) => {
        if (g.dx < -SWIPE_DISTANCE || g.vx < -SWIPE_VELOCITY) step(1);
        else if (g.dx > SWIPE_DISTANCE || g.vx > SWIPE_VELOCITY) step(-1);
      },
    });
  }, [behindSheet, slides.length]);

  const onLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    // A hidden web screen measures 0×0.
    if (!width || !height) return;
    session.frames[variant] = { width, height };
    setFrame((current) => (current?.width === width && current.height === height ? current : { width, height }));
  };

  const activeSlide = slides[active];

  return (
    <View className='flex-1 bg-color-tidal' onLayout={onLayout} testID={tid('panel')} {...swipe.panHandlers}>
      {/* NativeWind doesn't style Animated.View, so layout classes sit on plain wrappers. */}
      {slides.map((slide, i) => {
        // Waits for a frame, so a browser only fetches the crops it shows.
        const cover = frame && coverImage(slide.images, frame);
        return (
          <View key={slide.key} className='absolute inset-0 overflow-hidden' aria-hidden>
            <Animated.View style={{ flex: 1, opacity: opacities[i] }}>
              {cover && (
                <Image source={cover.source} resizeMode='cover' style={cover.style} testID={tid(`image-${slide.key}`)} />
              )}
              {variant === 'fullscreen' && slide.topScrim > 0 && (
                <View
                  className='pointer-events-none absolute inset-x-0 top-0 h-[160px]'
                  style={{ opacity: slide.topScrim }}
                >
                  <LinearGradient colors={[colors['scrim-media-end'], colors['scrim-media-start']]} style={{ flex: 1 }} />
                </View>
              )}
            </Animated.View>
          </View>
        );
      })}

      <View className='pointer-events-none absolute inset-x-0 bottom-0 h-1/2'>
        <LinearGradient colors={[colors['scrim-media-start'], colors['scrim-media-end']]} style={{ flex: 1 }} />
      </View>

      <View
        className={dsCn('absolute flex-row items-center justify-between', INSETS[variant].top)}
        style={{ marginTop: safeTop }}
      >
        <Logo
          variant='wordmark'
          width={variant === 'fullscreen' ? 96 : 104}
          color={behindSheet ? 'glass-on-dark-fg' : 'color-swift'}
        />
        <BaseButton
          onPress={togglePause}
          accessibilityLabel={paused ? t('Play slideshow') : t('Pause slideshow')}
          className='h-control-md w-control-md items-center justify-center overflow-hidden rounded-full bg-glass-on-dark'
          testID={tid('pause-button')}
        >
          <GlassBackdrop tone='on-dark' radius='rounded-full' />
          <View className={GLASS_CONTENT_LAYER}>
            <PlayPauseIcon paused={paused} color={colors['glass-on-dark-fg']} />
          </View>
        </BaseButton>
      </View>

      <View className={dsCn('absolute', INSETS[variant].bottom)} style={{ marginBottom: safeBottom }}>
        {/* Each dot's 30px target overhangs it by 12px, so the row's margin is 12px short. */}
        <View className={dsCn('-mx-[3px] flex-row items-center', variant === 'fullscreen' ? 'mb-ds-4' : 'mb-ds-12')}>
          {slides.map((slide, i) =>
            i === active ? (
              <View
                key={slide.key}
                className='mx-[3px] h-ds-6 overflow-hidden rounded-full'
                style={{ width: TRACK_WIDTH }}
                aria-hidden
              >
                <View className='absolute inset-0 bg-on-media-dark-foreground opacity-40' />
                <Animated.View
                  style={{
                    height: '100%',
                    width: progress.interpolate({ inputRange: [0, 1], outputRange: [0, TRACK_WIDTH] }),
                  }}
                >
                  <View className='flex-1 rounded-full bg-on-media-dark-foreground' />
                </Animated.View>
              </View>
            ) : (
              <BaseButton
                key={slide.key}
                onPress={() => goTo(i)}
                accessibilityLabel={t('Show slide {number}', { number: i + 1 })}
                // hitSlop doesn't reach the web, so the target is a box around the dot, as wide as the gaps allow.
                className='h-[30px] w-ds-12 items-center justify-center'
                testID={tid('dot', i)}
              >
                <View className='h-ds-6 w-ds-6 rounded-full bg-on-media-dark-foreground opacity-40' />
              </BaseButton>
            )
          )}
        </View>
        {activeSlide && <SlideTitle key={activeSlide.key} slide={activeSlide} variant={variant} />}
        {footer != null && <View className='mt-ds-24'>{footer}</View>}
      </View>
    </View>
  );
};
